import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ArrowLeft, Award, BadgePercent, BriefcaseBusiness, Home, LogOut, ReceiptText, UserRound } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "@/api/auth/authApi";
import { bookerPortalApi } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { setAuthToken } from "@/utils/axios";
import { formatBookerDate, formatMoney, getApiData, getApiMessage } from "@/utils/bookerFormat";
import { formatBookingDate, getBookingStatusLabel } from "@/utils/bookingFormat";
import { Input } from "@/components/ui/input";

const portalNav = [
  { to: "/booker/dashboard", label: "Dashboard", icon: Home },
  { to: "/booker/bookings", label: "Bookings", icon: BriefcaseBusiness },
  { to: "/booker/rewards", label: "Rewards", icon: Award },
  { to: "/booker/coupons", label: "Coupons", icon: BadgePercent },
  { to: "/booker/profile", label: "Profile", icon: UserRound },
];

export const BookerLoginPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await authApi.login({ identifier: form.identifier, password: form.password, rememberMe: true });
      const data = getApiData(response) || {};
      const token = data.token || data.Token;
      if (!token) throw new Error("Login response did not include an access token.");
      localStorage.setItem("accessToken", token);
      if (data.refreshToken || data.RefreshToken) localStorage.setItem("refreshToken", data.refreshToken || data.RefreshToken);
      setAuthToken(token);
      toast.success("Logged in successfully.");
      navigate("/booker/dashboard", { replace: true });
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to log in."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Booker login</title></Helmet>
      <main className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-10">
        <form onSubmit={submit} className="w-full max-w-md rounded-2xl border bg-background p-6 shadow-sm">
          <Link to="/" className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to home
          </Link>
          <h1 className="text-3xl font-bold">Booker login</h1>
          <p className="mt-1 text-muted-foreground">Access your hotel booking contribution and rewards.</p>
          <div className="mt-6 space-y-4">
            <Field label="Email or phone">
              <Input value={form.identifier} onChange={(e) => setForm((prev) => ({ ...prev, identifier: e.target.value }))} required />
            </Field>
            <Field label="Password">
              <Input type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} required />
            </Field>
            <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl">
              {loading ? "Logging in..." : "Log in"}
            </Button>
          </div>
        </form>
      </main>
    </>
  );
};

export const BookerPortalLayout = () => {
  const navigate = useNavigate();
  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setAuthToken(null);
    navigate("/booker/login", { replace: true });
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) navigate("/booker/login", { replace: true });
    else setAuthToken(token);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-muted/30">
      <aside className="fixed inset-y-0 left-0 hidden w-72 border-r bg-background p-5 lg:block">
        <div className="mb-8">
          <p className="text-sm text-muted-foreground">Hotel booker portal</p>
          <h1 className="text-xl font-bold">Booker rewards</h1>
        </div>
        <nav className="space-y-2">
          {portalNav.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <Button variant="outline" onClick={logout} className="absolute bottom-5 left-5 right-5 justify-start gap-2 rounded-xl">
          <LogOut className="h-4 w-4" />
          Log out
        </Button>
      </aside>
      <main className="lg:pl-72">
        <div className="mx-auto w-full max-w-6xl px-4 py-6">
          <div className="mb-5 flex gap-2 overflow-x-auto lg:hidden">
            {portalNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `whitespace-nowrap rounded-full px-4 py-2 text-sm ${isActive ? "bg-primary text-primary-foreground" : "bg-background"}`}>
                {item.label}
              </NavLink>
            ))}
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export const BookerDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadPortal(bookerPortalApi.dashboard, setData, setLoading); }, []);
  if (loading) return <Loading text="Loading dashboard..." />;
  return (
    <>
      <Helmet><title>Booker dashboard</title></Helmet>
      <PageTitle title="Dashboard" subtitle={`Welcome ${data?.booker?.name || "Booker"}`} />
      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Bookings brought" value={data?.totalBookings || 0} />
        <Metric label="Room nights" value={data?.totalRoomNights || 0} />
        <Metric label="Revenue brought" value={formatMoney(data?.totalRevenueBrought || 0)} />
        <Metric label="Available points" value={data?.availableRewardPoints || 0} />
      </div>
      <PortalTable title="Recent bookings" rows={data?.recentBookings || []} />
      <RewardList title="Recent rewards" rows={data?.recentTransactions || []} />
    </>
  );
};

export const BookerBookingsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadPortal(bookerPortalApi.bookings, setRows, setLoading); }, []);
  if (loading) return <Loading text="Loading bookings..." />;
  return <><PageTitle title="Bookings" subtitle="Bookings linked to your booker account." /><PortalTable title="Your bookings" rows={rows} /></>;
};

export const BookerRewardsHistoryPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadPortal(bookerPortalApi.rewards, setRows, setLoading); }, []);
  if (loading) return <Loading text="Loading rewards..." />;
  return <><PageTitle title="Rewards" subtitle="Your booker reward ledger." /><RewardList title="Reward history" rows={rows} /></>;
};

export const BookerCouponsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadPortal(bookerPortalApi.coupons, setRows, setLoading); }, []);
  if (loading) return <Loading text="Loading coupons..." />;
  return (
    <>
      <PageTitle title="Coupons" subtitle="Hotel-only coupons linked to your booker account." />
      <div className="grid gap-4 md:grid-cols-2">
        {rows.length ? rows.map((coupon) => (
          <Card key={coupon.id}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold">{coupon.couponCode}</p>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-sm text-primary">{coupon.usageRemaining} left</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{coupon.revenueCategory} · Valid until {formatBookerDate(coupon.validTo)}</p>
            </CardContent>
          </Card>
        )) : <Empty text="No coupons available." />}
      </div>
    </>
  );
};

export const BookerProfilePage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadPortal(bookerPortalApi.dashboard, (data) => {
      setDashboard(data);
      setForm(data?.booker || {});
    }, setLoading);
  }, []);
  const save = async () => {
    try {
      const response = await bookerPortalApi.updateProfile(form);
      setForm(getApiData(response));
      toast.success("Profile updated successfully.");
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to update profile."));
    }
  };
  if (loading) return <Loading text="Loading profile..." />;
  return (
    <>
      <PageTitle title="Profile" subtitle={dashboard?.booker?.name || "Booker profile"} />
      <div className="rounded-2xl border bg-background p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Contact person"><Input value={form.contactPersonName || ""} onChange={(e) => setForm({ ...form, contactPersonName: e.target.value })} /></Field>
          <Field label="Phone number"><Input value={form.phoneNumber || ""} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} /></Field>
          <Field label="Country"><Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
          <Field label="Website"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
          <Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
        </div>
        <Button onClick={save} className="mt-5 rounded-xl">Save profile</Button>
      </div>
    </>
  );
};

const loadPortal = async (fn, setData, setLoading) => {
  try {
    setLoading(true);
    const response = await fn();
    setData(getApiData(response) || []);
  } catch (error) {
    toast.error(getApiMessage(error, "Unable to load booker portal."));
  } finally {
    setLoading(false);
  }
};

const PageTitle = ({ title, subtitle }) => <div className="mb-6"><h1 className="text-3xl font-bold">{title}</h1><p className="mt-1 text-muted-foreground">{subtitle}</p></div>;
const Metric = ({ label, value }) => <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold">{value}</p></CardContent></Card>;
const Loading = ({ text }) => <div className="py-20 text-center text-muted-foreground">{text}</div>;
const Empty = ({ text }) => <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">{text}</div>;
const Field = ({ label, children }) => <label className="space-y-2"><span className="text-sm font-medium">{label}</span>{React.cloneElement(children, { className: "h-11 w-full rounded-xl border px-3 text-sm outline-none focus:ring-2 focus:ring-primary/20" })}</label>;

const PortalTable = ({ title, rows }) => (
  <section className="mt-6 rounded-2xl border bg-background p-5 shadow-sm">
    <h2 className="mb-4 text-lg font-semibold">{title}</h2>
    {rows.length ? <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-muted-foreground"><tr><th className="p-3">Booking</th><th className="p-3">Guest</th><th className="p-3">Stay</th><th className="p-3">Total</th><th className="p-3">Status</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id} className="border-t"><td className="p-3 font-medium">{row.bookingNumber}</td><td className="p-3">{row.guestName}</td><td className="p-3">{formatBookingDate(row.checkInDate)} - {formatBookingDate(row.checkOutDate)}</td><td className="p-3">{formatMoney(row.totalAmount)}</td><td className="p-3">{getBookingStatusLabel(row.status)}</td></tr>)}</tbody></table></div> : <Empty text="No bookings found." />}
  </section>
);

const RewardList = ({ title, rows }) => (
  <section className="mt-6 rounded-2xl border bg-background p-5 shadow-sm">
    <h2 className="mb-4 text-lg font-semibold">{title}</h2>
    {rows.length ? <div className="space-y-3">{rows.map((row) => <div key={row.id} className="flex items-center justify-between rounded-xl border p-4"><div><p className="font-medium">{row.description || "Reward entry"}</p><p className="text-sm text-muted-foreground">{formatBookerDate(row.createdAt)} · {formatMoney(row.eligibleRevenue)}</p></div><span className="font-semibold">{row.points} points</span></div>)}</div> : <Empty text="No reward entries found." />}
  </section>
);
